import {
  readSessionStorageJson,
  writeSessionStorageJson,
  SERVICE_FLOW_STORAGE_KEYS,
} from '../storage';
import { showToast, ToastType } from '../../toast';
import { replaceToken } from '../utils/replaceToken';

export const getItemKey = (item) =>
  String(item.serialNo || item.SerialNo || item.productName || item.ModelNo || '');

export class ItemRemoval {
  constructor({ container, translations, onItemsChanged }) {
    this._container = container;
    this._translations = translations || {};
    this._onItemsChanged = onItemsChanged;
    this._itemOrder = new Map();
  }

  setItemOrder(items) {
    this._itemOrder = new Map(items.map((item, index) => [getItemKey(item), index]));
  }

  removeCard(card, item) {
    const itemKey = getItemKey(item);
    const items = this._getItems();
    const itemIndex = items.findIndex((storedItem) => getItemKey(storedItem) === itemKey);

    if (!itemKey || itemIndex < 0) return;

    const remainingItems = items.filter((_, index) => index !== itemIndex);
    if (!writeSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, remainingItems)) return;

    card.remove();
    this._onItemsChanged?.();

    showToast({
      type: ToastType.WARNING,
      iconKey: 'settings',
      message: this._getDeletedItemMessage(item),
      actionLabel: this._translations.undoLabel,
      pauseOnInteraction: false,
      onAction: () => this._restoreCard(card, item),
    });
  }

  _restoreCard(card, item) {
    if (card.isConnected) return;

    const itemKey = getItemKey(item);
    const items = this._getItems();
    if (items.some((storedItem) => getItemKey(storedItem) === itemKey)) return;

    const restoredItems = [...items, item].sort(
      (left, right) => this._getItemOrder(left) - this._getItemOrder(right),
    );
    if (!writeSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, restoredItems)) return;

    const existingCards = [...this._container.querySelectorAll('[data-item-card]')];
    const targetOrder = this._getItemOrder(item);
    const nextCard = existingCards.find(
      (existingCard) => this._getItemOrder(this._getCardItem(existingCard)) > targetOrder,
    );

    if (nextCard) {
      this._container.insertBefore(card, nextCard);
    } else {
      this._container.appendChild(card);
    }

    this._onItemsChanged?.();
  }

  _getItems() {
    const items = readSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, []);
    return Array.isArray(items) ? items : [];
  }

  _getItemOrder(item) {
    return this._itemOrder.get(getItemKey(item)) ?? Number.MAX_SAFE_INTEGER;
  }

  _getCardItem(card) {
    try {
      return JSON.parse(card.dataset.itemCardConfig || '{}');
    } catch {
      return {};
    }
  }

  _getDeletedItemMessage(item) {
    const itemName = item.productName || item.ModelNo || item.serialNo || item.SerialNo || '';
    const template = this._translations.deletedMessageLabel;

    return replaceToken(template, 'itemName', itemName);
  }
}
